import React, { useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { useIsFocused, useRoute, RouteProp } from '@react-navigation/native';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { useResidentConversation } from '@/hooks/residents/useMessages';
import { useCombinedConversations, UnifiedConversation } from '@/hooks/residents/useCombinedConversations';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { AnchoredDropdown as RealAnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { SearchBar } from '@/components/common/admin/mobile/SearchBar';
import ResidentCreateConversation from '@/components/residents/propertyChat/ResidentCreateConversation';
import ResidentChatWindow from '@/components/residents/propertyChat/ResidentChatWindow';
import { EmptyMessageWindow } from '@/components/residents/propertyChat/EmptyMessageWindow';
import { AvatarCircle } from '@/components/common/AvatarCircle';
import { MESSAGE_SORT_OPTIONS, getSortOptionLabel, type MessageSortOption } from '@/constants/residents/messages';
import { MessagesStackNavigator } from '@/navigation/residents/mobile/MessagesStackNavigator';

export const MessagesScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  if (!isDesktop) {
    // Render the mobile messages stack navigator
    return <MessagesStackNavigator />;
  }

  return <DesktopMessagesView />;
};

const DesktopMessagesView: React.FC = () => {
  const isFocused = useIsFocused();
  const route = useRoute<RouteProp<DesktopMainParamList, 'Messages'>>();
  const initialSubject = route.params?.initialSubject;
  const existingConversationId = route.params?.existingConversationId;
  
  // Track selected property conversation
  const [selectedConversationId, setSelectedConversationId] = useState<number | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingSubject, setPendingSubject] = useState<string | undefined>(undefined);

  // Handle navigation params: either open existing conversation or show create form
  useEffect(() => {
    if (existingConversationId) {
      // Navigate to existing conversation
      setSelectedConversationId(existingConversationId);
      setShowCreate(false);
      setPendingSubject(undefined);
    } else if (initialSubject) {
      // Show create form with pre-filled subject
      setPendingSubject(initialSubject);
      setShowCreate(true);
      setSelectedConversationId(undefined);
    }
  }, [initialSubject, existingConversationId]);

  // Use combined conversations hook (property chat only)
  const {
    conversations,
    isLoading,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
  } = useCombinedConversations();

  // Get property conversation details when selected
  // Skip polling when screen is not focused to avoid unnecessary API calls
  const { conversation: propertyConversation } = useResidentConversation(selectedConversationId, { 
    skip: !isFocused 
  });

  // Sort dropdown (follow admin DesktopMessagesView pattern)
  const sortButtonRef = useRef<View | null>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const openSort = () => {
    if (sortButtonRef.current) {
      sortButtonRef.current.measureInWindow((x, y, width, height) => {
        setSortAnchor({ x, y, w: width, h: height });
        setIsSortOpen(true);
      });
    }
  };
  const closeSort = () => setIsSortOpen(false);

  // Handle conversation selection
  const handleConversationPress = (item: UnifiedConversation) => {
    const isCurrentlySelected = selectedConversationId === item.originalId;
    setSelectedConversationId(isCurrentlySelected ? undefined : item.originalId);
  };

  const renderConversationItem = ({ item }: { item: UnifiedConversation }) => {
    const isSelected = selectedConversationId === item.originalId;
    
    return (
      <TouchableOpacity
        onPress={() => handleConversationPress(item)}
        style={[styles.conversationItem, isSelected && styles.conversationItemSelected]}
      >
        <View style={styles.conversationAvatarWrap}>
          {item.isGroup ? (
            <RESIDENTS_SVG_COMPONENTS.GROUP width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} />
          ) : (
            <AvatarCircle
              firstName=""
              lastName=""
              size={32}
            />
          )}
        </View>
        <View style={styles.conversationTextWrap}>
          <Text style={styles.conversationSubject} numberOfLines={1}>{item.title}</Text>
          <Text style={styles.conversationPreview} numberOfLines={1}>{item.preview}</Text>
          <Text style={styles.conversationMeta} numberOfLines={1}>
            {item.recipientCount 
              ? `${item.recipientCount} Recipients` 
              : 'Property Chat'}
          </Text>
        </View>
        {item.hasUnread && <View style={styles.unreadDot} />}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.root}>
      <View style={styles.row}> 
        <View style={styles.leftPane}>
          <View style={styles.leftHeader}> 
            <SearchBar placeholder="Search" value={searchQuery} onChangeText={setSearchQuery} style={{ flex: 1 }} />
            <RNButton title="New Message" variant="tertiary" size="md" style={styles.newMessageBtn} onPress={() => setShowCreate(true)} />
          </View>
          <View style={styles.sortRow}> 
            <View style={styles.sortWrap} ref={sortButtonRef}>
              <TouchableOpacity style={styles.sortToggle} onPress={openSort} activeOpacity={0.8}>
                <Text style={styles.sortToggleText}>{getSortOptionLabel(sortOption as MessageSortOption)}</Text>
              </TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={conversations}
            keyExtractor={(item: UnifiedConversation) => item.id}
            renderItem={renderConversationItem}
            style={styles.list}
            contentContainerStyle={styles.listContent}
          />
        </View>
        <View style={styles.rightPane}>
          {showCreate ? (
            <ResidentCreateConversation 
              onClose={() => {
                setShowCreate(false);
                setPendingSubject(undefined);
              }}
              initialSubject={pendingSubject}
            />
          ) : !selectedConversationId ? (
            <EmptyMessageWindow />
          ) : propertyConversation ? (
            <View style={styles.chatWrap}>
              <View style={styles.chatHeader}>
                <View style={styles.chatHeaderText}>
                  <Text style={styles.chatHeaderTitle} numberOfLines={1}>{propertyConversation.subject}</Text>
                  <Text style={styles.chatHeaderSubtitle} numberOfLines={1}>{(() => {
                    const people = (propertyConversation.accounts || []).filter((a: any) => a.ConversationAccount?.type === 'resident');
                    if (people.length === 0) return 'No recipients';
                    if (people.length === 1) {
                      const p = people[0];
                      return p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.email;
                    }
                    return `${people.length} Recipients`;
                  })()}</Text>
                  <Text style={styles.chatHeaderMeta} numberOfLines={1}>{(() => {
                    const d = new Date(propertyConversation.createdAt);
                    const formattedDate = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    const formattedTime = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZoneName: 'short' });
                    return `Created ${formattedDate} ${formattedTime}`;
                  })()}</Text>
                </View>
              </View>
              <View style={styles.chatBody}>
                <ResidentChatWindow conversationId={selectedConversationId} />
              </View>
            </View>
          ) : (
            <EmptyMessageWindow />
          )}
        </View>
      </View>

      {/* Top-level dropdown to avoid clipping */}
      <RealAnchoredDropdown
        isOpen={isSortOpen}
        anchor={sortAnchor as any}
        width={(sortAnchor?.w || 280)}
        maxHeight={360}
        onRequestClose={closeSort}
      >
        <View>
          {MESSAGE_SORT_OPTIONS.map((option) => (
            <TouchableOpacity
              key={option.key}
              style={styles.sortMenuItem}
              onPress={() => {
                setSortOption(option.key as any);
                closeSort();
              }}
            >
              <Text style={styles.sortMenuItemText}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </RealAnchoredDropdown>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    padding: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    gap: 16,
    minHeight: 360,
    overflow: 'hidden',
  },
  leftPane: {
    // Match resio-web: 40% width
    width: '35%',
    alignSelf: 'stretch',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  rightPane: {
    // Match resio-web: remaining space (60% minus gap)
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  leftHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  newMessageBtn: {
    height: 40,
  },
  sortRow: {
    padding: 8,
  },
  sortWrap: {
    position: 'relative',
    flex: 1,
  },
  sortToggle: {
    borderRadius: 8,
    backgroundColor: '#1f1f1f',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  sortToggleText: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontWeight: '500',
  },
  sortMenuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  sortMenuItemText: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  list: { flex: 1 },
  listContent: { paddingBottom: 16 },
  conversationItem: {
    flexDirection: 'row',
    padding: 12,
    alignItems: 'center',
  },
  conversationItemSelected: {
    backgroundColor: '#1f1f1f',
  },
  conversationAvatarWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FF7629',
    marginLeft: 8,
  },
  conversationTextWrap: { marginLeft: 12, flex: 1 },
  conversationSubject: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    fontWeight: '700',
  },
  conversationPreview: {
    color: '#9CA3AF',
    fontSize: 13,
    marginTop: 2,
  },
  conversationMeta: {
    color: '#9CA3AF',
    fontSize: 12,
    marginTop: 2,
  },
  chatWrap: { flex: 1 },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  chatHeaderText: { flex: 1, marginRight: 12 },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  chatHeaderSubtitle: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  chatHeaderMeta: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  chatBody: { flex: 1 },
});
